type MaestroEventName = string;
type MaestroData = Record<string, unknown>;

export class Amplitude {
  static AMPLITUDE_EVENT_PREFIX = "[Amplitude]";

  EVENT_BUNDLE_ADDED = "bundle added";
  EVENT_CART_VIEWED = "click : cart viewed";
  EVENT_CLICK_ACCORDION = "click : accordion";
  EVENT_CLICK_ADD_FROM_MODULE = "click : add from module";
  EVENT_CLICK_ADD_FROM_PRODUCT_SELECTOR = "click : add from product selector";
  EVENT_CLICK_BREADCRUMB_LINK = "click : breadcrumb";
  EVENT_CLICK_CART_MODULE = "click : cart module";
  EVENT_CLICK_CONTINUE_TO_CHECKOUT = "click : continue to checkout";
  EVENT_CLICK_COUNTRY_SELECTION_MODAL = "click : country selection modal";
  EVENT_CLICK_LOCAL_NAV_LINK = "click : local navigation";
  EVENT_CLICK_MOBILE_MENU = "click : hamburger menu";
  EVENT_CLICK_MODULE = "click : module";
  EVENT_CLICK_NAV_LINK = "click : navigation bar";
  EVENT_CLICK_PRODUCT = "click : product";
  EVENT_CLICK_PRODUCT_IMAGE = "click : product image";
  EVENT_CLICK_PRODUCT_SELECTOR = "click : product combo selector";
  EVENT_CLICK_PRODUCT_SWATCH = "click : product swatch";
  EVENT_CLICK_PROMO_LINK = "click : site wide banner";
  EVENT_CLICK_QUANTITY_SELECTOR = "click : quantity selector";
  EVENT_CLICK_SIZE_SELECTOR = "click : size selector";
  EVENT_CLOSE_POPUP = "close : popup";
  EVENT_CONTACT_FORM_SUBMIT = "submit : contact us form";
  EVENT_FILL_OUT_POPUP = "fill out : popup";
  EVENT_HOVER_MODULE = "hover : module";
  EVENT_HOVER_NAV_LINK = "hover : navigation bar";
  EVENT_LOGIN_FORM_SUBMIT = "submit : login form";
  EVENT_ORDER_CREATED = "checkout : order created";
  EVENT_ORDER_SUBMITTED = "test : order submitted";
  EVENT_PRODUCT_ADDED = "product added";
  EVENT_PRODUCT_REMOVED = "product removed";
  EVENT_REGISTER_EXPERIMENT = "experiment : experiment assigned";
  EVENT_SUBMIT_POPUP = "submit : popup";
  EVENT_SUBMIT_POPUP_EMAIL = "submit : popup email";
  EVENT_SUBMIT_POPUP_SMS = "submit : popup phone";
  EVENT_SUBMIT_SITE_SEARCH = "submit : site search";
  EVENT_SUCCESSFUL_SIGN_OUT = "submit : logout";
  EVENT_UPSELL_PRODUCT_ADDED = "click : add from slideout";
  EVENT_VIEW_CART_PRODUCT_VARIANT_DRAWER = "view : cart product variant drawer";
  EVENT_VIEW_CART_UPSELLS = "view : up-sell slide-out";
  EVENT_CLICK_FILTER = "click : filter";
  EVENT_CLICK_SORT_BY = "click : sort by";
  EVENT_VIEW_CHECKOUT_PAGE = "test : checkout page";
  EVENT_VIEW_COUNTRY_SELECTION_MODAL = "view : country selection modal";
  EVENT_VIEW_ORDER_CONFIRMATION = "view : order confirmation";
  EVENT_VIEW_PAGE = "view : page";
  EVENT_VIEW_POPUP = "view : popup";
  EVENT_VIEW_PRODUCT_DETAILS = "view : product details";
  EVENT_VIEW_SEARCH_BAR = "view : search bar";
  EVENT_VIEW_SEARCH_RESULTS = "view : search results";

  get isAmplitudeAvailable() {
    return typeof window.amplitude?.track === "function";
  }

  waitForAmplitude() {
    let attempts = 0;

    if (this.isAmplitudeAvailable) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      const interval = setInterval(() => {
        if (this.isAmplitudeAvailable || attempts > 100) {
          clearInterval(interval as NodeJS.Timeout);

          const availability = attempts < 100;
          return resolve(availability);
        }
        attempts++;
      }, 60);
    });
  }

  get name() {
    return "Amplitude Browser SDK";
  }

  async logEvent(eventName: MaestroEventName, eventPayload: MaestroData) {
    if (!this.isAmplitudeAvailable) {
      const available = await this.waitForAmplitude();

      if (!available) {
        console.error("Amplitude failed to load");
        return;
      }
    }

    if (!("Shopify" in window)) {
      console.error("Shopify object is not defined");
      return;
    }

    console.info(eventName, eventPayload);

    window.amplitude.track(eventName, {
      ...eventPayload,
      market:
        eventPayload.market ?? document?.documentElement?.dataset?.countryCode,
    });
  }

  clickProduct(data: MaestroData) {
    this.logEvent(this.EVENT_CLICK_PRODUCT, data);
  }

  clickFilter(data: MaestroData) {
    this.logEvent(this.EVENT_CLICK_FILTER, data);
  }

  clickSortBy(data: MaestroData) {
    this.logEvent(this.EVENT_CLICK_SORT_BY, data);
  }

  clickAccordion(data: MaestroData) {
    this.logEvent(this.EVENT_CLICK_ACCORDION, data);
  }

  clickAddFromModule(data: MaestroData) {
    this.logEvent(this.EVENT_CLICK_ADD_FROM_MODULE, data);
  }

  clickAddFromProductSelector(data: MaestroData) {
    this.logEvent(this.EVENT_CLICK_ADD_FROM_PRODUCT_SELECTOR, data);
  }

  clickBreadcrumbLink(data: MaestroData) {
    this.logEvent(this.EVENT_CLICK_BREADCRUMB_LINK, data);
  }

  clickProductImage(data: MaestroData) {
    this.logEvent(this.EVENT_CLICK_PRODUCT_IMAGE, data);
  }

  clickHamburgerMenu(data: MaestroData) {
    this.logEvent(this.EVENT_CLICK_MOBILE_MENU, data);
  }

  clickProductSelector(data: MaestroData) {
    this.logEvent(this.EVENT_CLICK_PRODUCT_SELECTOR, data);
  }

  clickProductSwatch(data: MaestroData) {
    this.logEvent(this.EVENT_CLICK_PRODUCT_SWATCH, data);
  }

  clickPromoLink(data: MaestroData) {
    this.logEvent(this.EVENT_CLICK_PROMO_LINK, data);
  }

  clickQuantitySelector(data: MaestroData) {
    this.logEvent(this.EVENT_CLICK_QUANTITY_SELECTOR, data);
  }

  clickSizeSelector(data: MaestroData) {
    this.logEvent(this.EVENT_CLICK_SIZE_SELECTOR, data);
  }

  continueToCheckout(data: MaestroData) {
    this.logEvent(this.EVENT_CLICK_CONTINUE_TO_CHECKOUT, data);
  }

  clickLocalNavLink(data: MaestroData) {
    this.logEvent(this.EVENT_CLICK_LOCAL_NAV_LINK, data);
  }

  clickNavLink(data: MaestroData) {
    this.logEvent(this.EVENT_CLICK_NAV_LINK, data);
  }

  hoverNavLink(data: MaestroData) {
    this.logEvent(this.EVENT_HOVER_NAV_LINK, data);
  }

  viewPage(data: MaestroData) {
    this.logEvent(this.EVENT_VIEW_PAGE, data);
  }

  viewPopup(data: MaestroData) {
    this.logEvent(this.EVENT_VIEW_POPUP, data);
  }

  viewProductDetails(data: MaestroData) {
    this.logEvent(this.EVENT_VIEW_PRODUCT_DETAILS, data);
  }

  viewCartProductVariantDrawer(data: MaestroData) {
    this.logEvent(this.EVENT_VIEW_CART_PRODUCT_VARIANT_DRAWER, data);
  }

  viewOrderConfirmation(data: MaestroData) {
    this.logEvent(this.EVENT_VIEW_ORDER_CONFIRMATION, data);
  }

  viewCheckoutPage(data: MaestroData) {
    this.logEvent(this.EVENT_VIEW_CHECKOUT_PAGE, data);
  }

  viewCartUpsells(data: MaestroData) {
    this.logEvent(this.EVENT_VIEW_CART_UPSELLS, data);
  }

  fillOutPopup(data: MaestroData) {
    this.logEvent(this.EVENT_FILL_OUT_POPUP, data);
  }

  submitPopup(data: MaestroData) {
    this.logEvent(this.EVENT_SUBMIT_POPUP, data);
  }

  productAdded(data: MaestroData) {
    this.logEvent(this.EVENT_PRODUCT_ADDED, data);
  }

  productRemoved(data: MaestroData) {
    this.logEvent(this.EVENT_PRODUCT_REMOVED, data);
  }

  clickAddFromSlideout(data: MaestroData) {
    this.logEvent(this.EVENT_UPSELL_PRODUCT_ADDED, data);
  }

  orderCreated(data: MaestroData) {
    this.logEvent(this.EVENT_ORDER_CREATED, data);
  }

  orderSubmitted(data: MaestroData) {
    this.logEvent(this.EVENT_ORDER_SUBMITTED, data);
  }

  submitContactForm(data: MaestroData) {
    this.logEvent(this.EVENT_CONTACT_FORM_SUBMIT, data);
  }

  cartViewed(data: MaestroData) {
    this.logEvent(this.EVENT_CART_VIEWED, data);
  }

  submitLoginForm(data: MaestroData) {
    this.logEvent(this.EVENT_LOGIN_FORM_SUBMIT, data);
  }

  successfulSignOut(data: MaestroData) {
    this.logEvent(this.EVENT_SUCCESSFUL_SIGN_OUT, data);
  }

  viewCountrySelectionModal(data: MaestroData) {
    this.logEvent(this.EVENT_VIEW_COUNTRY_SELECTION_MODAL, data);
  }

  clickCountrySelectionModal(data: MaestroData) {
    this.logEvent(this.EVENT_CLICK_COUNTRY_SELECTION_MODAL, data);
  }

  loadedExperiment(data: MaestroData) {
    const registeredExperiments =
      (window.localStorage.registeredExperiments &&
        JSON.parse(window.localStorage.registeredExperiments)) ||
      [];

    if (
      registeredExperiments.filter(
        (ex: string) => JSON.stringify(ex) === JSON.stringify(data)
      ).length === 0
    ) {
      const amplitudeLoop = setInterval(() => {
        this.logEvent(this.EVENT_REGISTER_EXPERIMENT, data);
        registeredExperiments.push(data);
        window.localStorage.setItem(
          "registeredExperiments",
          JSON.stringify(registeredExperiments)
        );
        clearInterval(amplitudeLoop as NodeJS.Timeout);
      }, 100);
    }
  }

  attentiveSubmitPopupEmail(data: MaestroData) {
    this.logEvent(this.EVENT_SUBMIT_POPUP_EMAIL, {
      "event label": data.eventCategory,
      variant: data.eventLabel,
    });
  }

  attentiveSubmitPopupSMS(data: MaestroData) {
    this.logEvent(this.EVENT_SUBMIT_POPUP_SMS, {
      "event label": data.eventCategory,
      variant: data.eventLabel,
    });
  }

  attentiveViewPopup(data: MaestroData) {
    this.logEvent(this.EVENT_VIEW_POPUP, {
      "event label": data.eventCategory,
      variant: data.eventLabel,
    });
  }

  attentiveClosePopup(data: MaestroData) {
    this.logEvent(this.EVENT_CLOSE_POPUP, {
      "event label": data.eventCategory,
      variant: data.eventLabel,
    });
  }

  clickModule(data: MaestroData) {
    this.logEvent(this.EVENT_CLICK_MODULE, data);
  }

  clickCartModule(data: MaestroData) {
    this.logEvent(this.EVENT_CLICK_CART_MODULE, data);
  }

  hoverModule(data: MaestroData) {
    this.logEvent(this.EVENT_HOVER_MODULE, data);
  }

  submitSiteSearch(data: MaestroData) {
    this.logEvent(this.EVENT_SUBMIT_SITE_SEARCH, data);
  }

  viewSearchBar(data: MaestroData) {
    this.logEvent(this.EVENT_VIEW_SEARCH_BAR, data);
  }

  viewSearchResults(data: MaestroData) {
    this.logEvent(this.EVENT_VIEW_SEARCH_RESULTS, data);
  }
}
